import React, { useState } from 'react';
import { Typography, Tag, Flex, List, Button, theme } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';

const { Text } = Typography;

const createMarkup = (markUp) => {
  return { __html: markUp };
};

const COLLAPSED_COUNT = 2; // how many rules to show when collapsed

export const TableDescription = ({
  formNo,
  formName,
  maxPoints,
  rules,
  cumulativeApaPoints,
  planningMaxEstimatedPoint,
  cameFrom,
  isEvaluation
}) => {
  const { token } = theme.useToken();
  const [expanded, setExpanded] = useState(false);

  const visibleRules = rules?.filter(rule => {
    if (typeof rule === 'string') return true; // backward compatibility
    if (rule.evaluationOnly && !isEvaluation) return false;
    if (rule.planningOnly && isEvaluation) return false;
    return true;
  }) || [];

  const hasOverflow = visibleRules.length > COLLAPSED_COUNT;
  const displayedRules = expanded
    ? visibleRules
    : visibleRules.slice(0, COLLAPSED_COUNT);

  return (
    <List
      header={
        <Flex
          align="center"
          justify="space-between"
          wrap="wrap"
          gap={8}
          style={{
            padding: '8px 12px',
            background: token.colorBgContainer,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: 8,
          }}
        >
          <Text
            strong
            style={{
              fontSize: 14,
              color: token.colorText,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 280,
            }}
            title={`${formNo} ~ ${formName}`}
          >
            {formNo} ~ {formName}
          </Text>

          <Flex gap={6} wrap="wrap" align="center">
            <Flex align="center" gap={4}>
              <Text type="secondary" style={{ fontSize: 11 }}>Max:</Text>
              <Tag color="blue" style={{ margin: 0, fontSize: 11, lineHeight: '18px', padding: '0 6px' }}>
                {maxPoints ?? '...'}
              </Tag>
            </Flex>

            {cameFrom !== 'faculty' && (
              <Flex align="center" gap={4}>
                <Text type="secondary" style={{ fontSize: 11 }}>Achievable:</Text>
                <Tag color="green" style={{ margin: 0, fontSize: 11, lineHeight: '18px', padding: '0 6px' }}>
                  {cumulativeApaPoints || planningMaxEstimatedPoint || '...'}
                </Tag>
              </Flex>
            )}
          </Flex>
        </Flex>
      }
      dataSource={displayedRules}
      footer={
        hasOverflow ? (
          <Flex justify="center">
            <Button
              type="link"
              size="small"
              onClick={() => setExpanded((prev) => !prev)}
              icon={expanded ? <UpOutlined /> : <DownOutlined />}
              iconPosition="end"
              style={{ fontSize: 12 }}
            >
              {expanded
                ? 'See less'
                : `See more (${visibleRules.length - COLLAPSED_COUNT} more)`}
            </Button>
          </Flex>
        ) : null
      }
      renderItem={(item) => {
        const rawText = typeof item === 'string' ? item : item.text;
        const noBullet = rawText.startsWith('NO_BULLET:');
        const content = noBullet ? rawText.replace('NO_BULLET:', '') : rawText;

        return (
          <List.Item style={{ padding: '4px 0', borderBottom: 'none', maxWidth: '100%', boxSizing: 'border-box' }}>
            <Flex gap={8} align="flex-start" style={{ maxWidth: '100%', width: '100%', minWidth: 0 }}>
              {!noBullet && <span style={{ marginTop: 2, flexShrink: 0, fontSize: 12 }}>•</span>}
              <div
                dangerouslySetInnerHTML={createMarkup(content)}
                style={{
                  flex: 1,
                  minWidth: 0,
                  maxWidth: '100%',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                  fontSize: 14,
                  color: token.colorText,
                }}
              />
            </Flex>
          </List.Item>
        );
      }}
    />
  );
};